import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from './ProjectsContext';
import { useAuth } from './AuthContext';
import jsPDF from 'jspdf';

export default function ExistingProjects() {
  const navigate = useNavigate();
  const { projects, deleteProject } = useProjects();
  const { currentUser, logout } = useAuth();

  // Approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalProjectId, setApprovalProjectId] = useState(null);
  const [approvalProjectName, setApprovalProjectName] = useState('');
  const [approverEmails, setApproverEmails] = useState(['']);
  const [sendingApproval, setSendingApproval] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Generate PDF and return the jsPDF doc object
  const generatePDF = (project) => {
    const doc = new jsPDF('landscape');
    const lineHeight = 6;
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 15;
    const contentWidth = pageWidth - (2 * margin);

    const colorMap = {
      green: [76, 175, 80],
      amber: [255, 193, 7],
      red: [244, 67, 54],
      grey: [158, 158, 158]
    };

    const addSectionTitle = (title, x, y, underlineWidth = 60) => {
      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(76, 175, 80);
      doc.text(title, x, y);
      doc.setDrawColor(76, 175, 80);
      doc.setLineWidth(0.5);
      doc.line(x, y + 1, x + underlineWidth, y + 1);
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);
      return y + 9;
    };

    // ========== PAGE 1: PROJECT INFO (LEFT) + RAG ASSESSMENT (RIGHT) ==========
    doc.setFillColor(76, 175, 80);
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    const bannerText = `${project.stpType || 'Project'} - ${project.location?.country || 'N/A'}, ${project.location?.city || 'N/A'}`;
    doc.text(bannerText, pageWidth / 2, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    const leftColumnX = margin;
    const leftColumnWidth = (contentWidth / 2) - 10;
    const rightColumnX = leftColumnX + leftColumnWidth + 20;
    const rightColumnWidth = leftColumnWidth;

    let leftY = 45;
    let rightY = 45;

    // LEFT COLUMN - Project Details
    leftY = addSectionTitle('PROJECT DESCRIPTION', leftColumnX, leftY, 70);
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    const descLines = doc.splitTextToSize(project.description || 'N/A', leftColumnWidth);
    descLines.forEach(line => {
      doc.text(line, leftColumnX, leftY);
      leftY += lineHeight;
    });
    leftY += lineHeight;

    if (project.stpType === 'STP0') {
      leftY = addSectionTitle('DRIVER', leftColumnX, leftY, 40);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const driverLines = doc.splitTextToSize(project.driver || 'N/A', leftColumnWidth);
      driverLines.forEach(line => { doc.text(line, leftColumnX, leftY); leftY += lineHeight; });
      leftY += lineHeight;

      leftY = addSectionTitle('RECOMMENDATION', leftColumnX, leftY, 60);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const recLines = doc.splitTextToSize(project.recommendation || 'N/A', leftColumnWidth);
      recLines.forEach(line => { doc.text(line, leftColumnX, leftY); leftY += lineHeight; });
      leftY += lineHeight;

      leftY = addSectionTitle('PRE-SPEND REQUEST', leftColumnX, leftY, 70);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const preSpendLines = doc.splitTextToSize(project.preSpend || 'N/A', leftColumnWidth);
      preSpendLines.forEach(line => { doc.text(line, leftColumnX, leftY); leftY += lineHeight; });
      leftY += lineHeight;

      leftY = addSectionTitle('NEXT STEPS', leftColumnX, leftY, 50);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const nextStepsLines = doc.splitTextToSize(project.nextSteps || 'N/A', leftColumnWidth);
      nextStepsLines.forEach(line => { doc.text(line, leftColumnX, leftY); leftY += lineHeight; });
      leftY += lineHeight;
    }

    if (project.stpType === 'STP1' || project.stpType === 'STP2') {
      leftY = addSectionTitle('DRIVER', leftColumnX, leftY, 40);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const driverLines = doc.splitTextToSize(project.driver || 'N/A', leftColumnWidth);
      driverLines.forEach(line => { doc.text(line, leftColumnX, leftY); leftY += lineHeight; });
      leftY += lineHeight;

      leftY = addSectionTitle('DECISION', leftColumnX, leftY, 40);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const decisionLines = doc.splitTextToSize(project.decision || 'N/A', leftColumnWidth);
      decisionLines.forEach(line => { doc.text(line, leftColumnX, leftY); leftY += lineHeight; });
      leftY += lineHeight;

      leftY = addSectionTitle('GOA', leftColumnX, leftY, 40);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');

      const leaseValue = parseFloat(project.goa?.leaseLiability) || 0;
      const leaseFormatted = leaseValue > 0 ? leaseValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : (project.goa?.leaseLiability || 'N/A');
      doc.text(`Lease Liability: \u00A3${leaseFormatted}`, leftColumnX, leftY);
      leftY += lineHeight;

      const capexValue = parseFloat(project.goa?.capexLiability) || 0;
      const capexFormatted = capexValue > 0 ? capexValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : (project.goa?.capexLiability || 'N/A');
      doc.text(`Capex Liability: \u00A3${capexFormatted}`, leftColumnX, leftY);
      leftY += lineHeight;

      const total = leaseValue + capexValue;
      doc.setFont(undefined, 'bold');
      doc.text(`Total: \u00A3${total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, leftColumnX, leftY);
      doc.setFont(undefined, 'normal');
      leftY += lineHeight * 2;

      leftY = addSectionTitle('P&L IMPACT', leftColumnX, leftY, 50);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const pnlLines = doc.splitTextToSize(project.pnlImpact || 'N/A', leftColumnWidth);
      pnlLines.forEach(line => { doc.text(line, leftColumnX, leftY); leftY += lineHeight; });
      leftY += lineHeight;
    }

    // RIGHT COLUMN - RAG Assessment
    if (project.rag && Object.keys(project.rag).length > 0) {
      rightY = addSectionTitle(`RAG ASSESSMENT ${project.stpType === 'STP0' ? '(CURRENT)' : '(PROPOSED)'}`, rightColumnX, rightY, 100);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(240, 240, 240);
      doc.rect(rightColumnX, rightY - 7, rightColumnWidth, 9, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(rightColumnX, rightY - 7, rightColumnWidth, 9);
      doc.text('Item', rightColumnX + 2, rightY);
      doc.text('RAG', rightColumnX + rightColumnWidth - 20, rightY);
      rightY += 9;
      doc.setFont(undefined, 'normal');

      Object.entries(project.rag).forEach(([item, color]) => {
        doc.setFillColor(255, 255, 255);
        doc.rect(rightColumnX, rightY - 7, rightColumnWidth, 8, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.rect(rightColumnX, rightY - 7, rightColumnWidth, 8);
        doc.text(item, rightColumnX + 2, rightY);
        const rgb = colorMap[color] || [128, 128, 128];
        doc.setFillColor(rgb[0], rgb[1], rgb[2]);
        doc.roundedRect(rightColumnX + rightColumnWidth - 15, rightY - 4, 10, 5, 1, 1, 'F');
        rightY += 8;
      });
    }

    // ========== PAGE 2: FINANCIAL SUMMARY ==========
    if (project.financialSummary && project.financialSummary.length > 0) {
      doc.addPage('landscape');
      doc.setFillColor(76, 175, 80);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('FINANCIAL SUMMARY', pageWidth / 2, 16, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      let tableY = 35;

      if (project.stpType === 'STP0' && project.modelDate) {
        tableY = addSectionTitle('MODEL DATE', margin, tableY, 50);
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        doc.text(`From: ${project.modelDate?.from || 'N/A'}  |  To: ${project.modelDate?.to || 'N/A'}`, margin, tableY);
        tableY += lineHeight * 3;
      }

      const numCols = project.financialSummary[0].length;
      const firstColWidth = 80;
      const fourthColWidth = 30;
      const remainingWidth = contentWidth - firstColWidth - fourthColWidth;
      const otherColWidth = remainingWidth / (numCols - 2);

      project.financialSummary.forEach((row, rowIndex) => {
        const boldRowIndices = [0, 2, 8];
        const isBoldRow = boldRowIndices.includes(rowIndex);
        const rowHeight = 12;
        let xPos = margin;

        if (rowIndex % 2 === 0) {
          doc.setFillColor(250, 250, 250);
          doc.rect(margin, tableY - 8, contentWidth, rowHeight, 'F');
        }

        row.forEach((cell, cellIndex) => {
          let currentColWidth = otherColWidth;
          if (cellIndex === 0) currentColWidth = firstColWidth;
          if (cellIndex === 3) currentColWidth = fourthColWidth;

          if (isBoldRow || cell.style?.bold) {
            doc.setFont(undefined, 'bold');
          } else {
            doc.setFont(undefined, 'normal');
          }

          doc.setFontSize(9);
          doc.setDrawColor(180, 180, 180);
          doc.setLineWidth(0.1);
          doc.rect(xPos, tableY - 8, currentColWidth, rowHeight);

          const cellValue = String(cell.value || '');
          const alignment = cell.style?.alignment?.horizontal || 'left';
          let textX = xPos + 2;
          let alignOption = 'left';
          if (alignment === 'center') { textX = xPos + currentColWidth / 2; alignOption = 'center'; }
          else if (alignment === 'right') { textX = xPos + currentColWidth - 2; alignOption = 'right'; }

          doc.text(cellValue, textX, tableY, { align: alignOption, maxWidth: currentColWidth - 4 });
          xPos += currentColWidth;
        });
        tableY += rowHeight;
      });
      doc.setFont(undefined, 'normal');
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
    }

    // ========== PAGE 3: RISK ITEMS ==========
    const riskItemsFiltered = project.riskItems && Object.keys(project.riskItems).length > 0
      ? Object.entries(project.riskItems).filter(([item]) => {
          const ragColor = project.rag?.[item];
          return ragColor === 'amber' || ragColor === 'red';
        })
      : [];

    if (riskItemsFiltered.length > 0) {
      doc.addPage('landscape');
      doc.setFillColor(76, 175, 80);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('PROJECT RISK ITEMS', pageWidth / 2, 16, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      let riskY = 40;

      riskItemsFiltered.forEach(([item, note]) => {
        const ragColor = project.rag?.[item] || 'red';
        const rgb = colorMap[ragColor] || [244, 67, 54];

        doc.setFont(undefined, 'bold');
        doc.setFontSize(13);
        doc.setTextColor(rgb[0], rgb[1], rgb[2]);
        doc.text(`\u2022 ${item}`, margin, riskY);
        riskY += lineHeight + 3;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(11);
        doc.setTextColor(60, 60, 60);
        const noteLines = doc.splitTextToSize(note || 'No details provided', contentWidth - 10);
        noteLines.forEach(line => { doc.text(line, margin + 5, riskY); riskY += lineHeight; });
        doc.setTextColor(0, 0, 0);
        riskY += lineHeight * 2;

        if (riskY > pageHeight - margin - 40) {
          doc.addPage('landscape');
          doc.setFillColor(76, 175, 80);
          doc.rect(0, 0, pageWidth, 25, 'F');
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(255, 255, 255);
          doc.text('PROJECT RISK ITEMS (continued)', pageWidth / 2, 16, { align: 'center' });
          doc.setTextColor(0, 0, 0);
          riskY = 40;
        }
      });
    }

    // ========== APPROVAL STAMPS PAGE (if project is approved) ==========
    if (project.status === 'approved' && project.approvalData && project.approvalData.length > 0) {
      doc.addPage('landscape');

      // Header
      doc.setFillColor(76, 175, 80);
      doc.rect(0, 0, pageWidth, 30, 'F');
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('APPROVAL CONFIRMATION', pageWidth / 2, 20, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      let stampY = 50;

      // "APPROVED" watermark text
      doc.setFontSize(60);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(76, 175, 80, 40);
      doc.text('APPROVED', pageWidth / 2, stampY, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      stampY += 30;

      // Project name
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      const projectTitle = `${project.stpType} - ${project.location?.city || ''}, ${project.location?.country || ''}`;
      doc.text(projectTitle, pageWidth / 2, stampY, { align: 'center' });
      stampY += 20;

      // Approval details
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('This project has been approved by the following:', margin, stampY);
      stampY += 15;

      project.approvalData.forEach((approver, index) => {
        // Approver box
        doc.setFillColor(245, 250, 245);
        doc.setDrawColor(76, 175, 80);
        doc.setLineWidth(0.5);
        doc.rect(margin, stampY - 8, contentWidth, 30, 'FD');

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Approver ${index + 1}:`, margin + 5, stampY);

        doc.setFont(undefined, 'normal');
        doc.setFontSize(11);
        doc.text(`Name: ${approver.name}`, margin + 5, stampY + 8);
        doc.text(`Email: ${approver.email}`, margin + 5, stampY + 16);

        const approvedDate = approver.approvedAt
          ? new Date(approver.approvedAt).toLocaleString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })
          : 'N/A';
        doc.text(`Approved: ${approvedDate}`, margin + contentWidth / 2, stampY + 8);

        // Green tick
        doc.setFontSize(20);
        doc.setTextColor(76, 175, 80);
        doc.text('\u2713', margin + contentWidth - 20, stampY + 10);
        doc.setTextColor(0, 0, 0);

        stampY += 40;
      });
    }

    return doc;
  };

  const handleDownloadPDF = (project) => {
    const doc = generatePDF(project);
    const locationName = `${project.location?.city || 'Unknown'}_${project.location?.country || 'Unknown'}`;
    const fileName = `${locationName.replace(/[^a-z0-9]/gi, '_')}.pdf`;
    doc.save(fileName);
  };

  const handleSendApproval = (project) => {
    const projectName = `${project.stpType} - ${project.location?.city || ''}, ${project.location?.country || ''}`;
    setApprovalProjectId(project.id);
    setApprovalProjectName(projectName);
    setApproverEmails(['']);
    setShowApprovalModal(true);
  };

  const addEmailField = () => {
    setApproverEmails([...approverEmails, '']);
  };

  const removeEmailField = (index) => {
    setApproverEmails(approverEmails.filter((_, i) => i !== index));
  };

  const updateEmail = (index, value) => {
    const updated = [...approverEmails];
    updated[index] = value;
    setApproverEmails(updated);
  };

  const handleSubmitApproval = async () => {
    const validEmails = approverEmails.filter(e => e.trim() !== '');
    if (validEmails.length === 0) {
      alert('Please enter at least one email address.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmail = validEmails.find(e => !emailRegex.test(e.trim()));
    if (invalidEmail) {
      alert(`Invalid email address: ${invalidEmail}`);
      return;
    }

    setSendingApproval(true);

    try {
      // Find the full project to generate PDF
      const project = projects.find(p => p.id === approvalProjectId);
      if (!project) throw new Error('Project not found');

      // Generate PDF and get base64
      const doc = generatePDF(project);
      const pdfBase64 = doc.output('datauristring').split(',')[1];

      // Get the current site URL
      const siteUrl = window.location.origin;

      // Call Netlify Function to send approval emails
      const response = await fetch('/.netlify/functions/send-approval', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: approvalProjectId,
          projectName: approvalProjectName,
          approverEmails: validEmails,
          pdfBase64,
          siteUrl
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send approval requests');
      }

      alert(`Approval requests sent to ${validEmails.length} recipient(s).`);
      setShowApprovalModal(false);
    } catch (error) {
      console.error('Error sending approval:', error);
      alert('Error sending approval: ' + error.message);
    } finally {
      setSendingApproval(false);
    }
  };

  const handleEdit = (project) => {
    navigate('/new-project', { state: { project } });
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: { bg: '#4CAF50', text: 'Approved', icon: '\u2713' },
      pending: { bg: '#FF9800', text: 'Pending Approval', icon: '\u23F3' },
      rejected: { bg: '#F44336', text: 'Rejected', icon: '\u2717' },
      draft: { bg: '#9E9E9E', text: 'Draft', icon: '' }
    };
    const s = styles[status] || styles.draft;
    return (
      <span style={{
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: s.bg,
        color: 'white',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {s.icon} {s.text}
      </span>
    );
  };

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Return to Home
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#666', fontSize: '14px' }}>{currentUser?.username}</span>
          <button
            type="button"
            onClick={handleLogout}
            style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
          >
            Logout
          </button>
        </div>
      </div>
      <h1 style={{ textAlign: 'center', color: '#333' }}>Existing Projects</h1>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {projects.map(project => (
          <li key={project.id} style={{
            border: '1px solid #ddd',
            padding: '15px',
            marginBottom: '15px',
            borderRadius: '4px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#333' }}>{project.location.city}, {project.location.country} - {project.stpType}</h3>
            <p style={{ color: '#666' }}>Status: {getStatusBadge(project.status)}</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => handleSendApproval(project)} style={{ padding: '8px 12px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Send for Approval</button>
              <button onClick={() => handleDownloadPDF(project)} style={{ padding: '8px 12px', backgroundColor: '#FF9800', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Download PDF</button>
              <button onClick={() => handleEdit(project)} style={{ padding: '8px 12px', backgroundColor: '#9C27B0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Edit</button>
              <button onClick={() => deleteProject(project.id)} style={{ padding: '8px 12px', backgroundColor: '#F44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button>
            </div>
          </li>
        ))}
      </ul>

      {/* Approval Email Modal */}
      {showApprovalModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', padding: '30px',
            width: '500px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ color: '#333', marginTop: 0 }}>Send for Approval</h2>
            <p style={{ color: '#666' }}>Project: <strong>{approvalProjectName}</strong></p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Enter the email addresses of the approvers. All approvers must approve for the project to be approved.
            </p>

            {approverEmails.map((email, index) => (
              <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => updateEmail(index, e.target.value)}
                  placeholder="approver@example.com"
                  style={{
                    flex: 1, padding: '10px', border: '1px solid #ddd',
                    borderRadius: '4px', fontSize: '14px'
                  }}
                />
                {approverEmails.length > 1 && (
                  <button
                    onClick={() => removeEmailField(index)}
                    style={{
                      padding: '8px 12px', backgroundColor: '#F44336', color: 'white',
                      border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px'
                    }}
                  >
                    X
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={addEmailField}
              style={{
                padding: '8px 16px', backgroundColor: '#e0e0e0', color: '#333',
                border: 'none', borderRadius: '4px', cursor: 'pointer',
                marginBottom: '20px', fontSize: '14px'
              }}
            >
              + Add another approver
            </button>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setShowApprovalModal(false)}
                disabled={sendingApproval}
                style={{
                  padding: '10px 20px', backgroundColor: '#6c757d', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitApproval}
                disabled={sendingApproval}
                style={{
                  padding: '10px 20px',
                  backgroundColor: sendingApproval ? '#ccc' : '#4CAF50',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: sendingApproval ? 'not-allowed' : 'pointer'
                }}
              >
                {sendingApproval ? 'Sending...' : 'Send Approval Requests'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
